import { Fragment, type CSSProperties, type ReactNode } from 'react';

import { labelClipWrapper, labelSpan } from './BarLabelOverlay.sty.js';
import { BAR_LABEL_MIN_SIZE, BAR_LABEL_PADDING } from './label-utils.js';
import type { TruncationMode } from '../../../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth12pixelsSize400Map } from '../../../../core/components/text-ellipsis/chars-font-width-map.js';
import { truncateString } from '../../../../core/components/text-ellipsis/truncate-string.js';

interface VerticalBarLabelProps {
  x: number;
  y: number;
  barWidth: number;
  barHeight: number;
  canvasHeight: number;
  isReversed: boolean;
  text: string;
  insideColor: string;
  outsideColor: string;
  truncationMode: TruncationMode;
}

/**
 * HTML "split-clip" label for a single vertical bar.
 *
 * Mirrors the horizontal split-clip pattern but rotated. The constraining
 * dimension is the bar's WIDTH (perpendicular to the rotated text's reading
 * direction); the bar's height does not constrain a rotated label — the
 * available text length runs the full chart height minus padding.
 *
 * When `isReversed` is false (BTT, normal) the bar grows upward and the label
 * is rotated −90°, reading bottom-to-top.
 *
 * When `isReversed` is true (the numerical Y axis has `reversed` set, i.e.
 * TTB) the bar grows downward from the chart top and the label is rotated
 * +90°, reading top-to-bottom.
 */
export function VerticalBarLabel({
  x,
  y,
  barWidth,
  barHeight,
  canvasHeight,
  isReversed,
  text,
  insideColor,
  outsideColor,
  truncationMode,
}: Readonly<VerticalBarLabelProps>): ReactNode {
  // For vertical bars the rotated text's *height* (font line-box) sits across
  // the bar's WIDTH. The bar height does not constrain a rotated label.
  if (barWidth < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const availLength = canvasHeight - 2 * BAR_LABEL_PADDING;
  if (availLength < BAR_LABEL_MIN_SIZE) {
    return null;
  }

  const truncated = truncateString(
    text,
    availLength,
    charsFontWidth12pixelsSize400Map,
    truncationMode,
  );

  const centerX = x + barWidth / 2;
  const centerY = canvasHeight / 2;

  const rotation = isReversed ? 'rotate(90deg)' : 'rotate(-90deg)';

  const outerStyle: CSSProperties = {
    left: centerX,
    top: centerY,
    transform: `translate(-50%, -50%) ${rotation}`,
    width: availLength,
    color: outsideColor,
  };
  const wrapperStyle: CSSProperties = {
    left: x,
    top: y,
    width: barWidth,
    height: barHeight,
  };
  // The inner span must sit at the same absolute position as the outer span,
  // expressed relative to the clip wrapper's origin at (x, y).
  const innerStyle: CSSProperties = {
    left: barWidth / 2,
    top: centerY - y,
    transform: `translate(-50%, -50%) ${rotation}`,
    width: availLength,
    color: insideColor,
  };

  return (
    <Fragment>
      <span className={labelSpan} style={outerStyle}>
        {truncated}
      </span>
      <div className={labelClipWrapper} style={wrapperStyle}>
        <span className={labelSpan} style={innerStyle}>
          {truncated}
        </span>
      </div>
    </Fragment>
  );
}

VerticalBarLabel['displayName'] = 'VerticalBarLabel';
